import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Mail, 
  Send, 
  Clock, 
  CheckCircle, 
  Trash2, 
  Edit, 
  Eye,
  X,
  FileText,
  Users,
  Building,
  Calendar,
  Copy,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'general', label: 'Général', color: 'bg-blue-100 text-blue-700' },
  { id: 'monthly_report', label: 'Rapport mensuel', color: 'bg-green-100 text-green-700' },
  { id: 'event', label: 'Événement', color: 'bg-purple-100 text-purple-700' },
  { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const Newsletters = () => {
  const { token } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('newsletters');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [filter, setFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    content: '',
    building_ids: [],
    target_roles: ['coproprietaire'],
    scheduled_at: ''
  });

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    content: '',
    category: 'general'
  });

  const fetchNewsletters = useCallback(async () => {
    try {
      let url = `${API_URL}/api/newsletters?limit=50`;
      if (filter !== 'all') url += `&status=${filter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewsletters(data.newsletters || []);
      }
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    }
  }, [token, filter]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/newsletters/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [token]);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/buildings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBuildings(data.buildings || data || []);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token]);

  const seedDefaultTemplates = async () => {
    try {
      await fetch(`${API_URL}/api/newsletters/templates/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error seeding templates:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchNewsletters(), fetchTemplates(), fetchBuildings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchNewsletters, fetchTemplates, fetchBuildings]);

  useEffect(() => {
    // Seed default templates if none exist
    if (templates.length === 0 && !loading) {
      seedDefaultTemplates();
    }
  }, [templates, loading]);

  const handlePreview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/newsletters/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Error previewing:', error);
    }
  };

  const handleCreate = async (sendImmediately = false) => {
    if (!formData.title || !formData.subject || !formData.content) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = { ...formData };
      if (sendImmediately) {
        payload.scheduled_at = null;
      }

      const res = await fetch(`${API_URL}/api/newsletters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowCreateModal(false);
        resetForm();
        fetchNewsletters();
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating newsletter:', error);
    }
  };

  const handleSend = async (newsletterId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir envoyer cette newsletter maintenant ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/newsletters/${newsletterId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchNewsletters();
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
    }
  };

  const handleDelete = async (newsletterId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette newsletter ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/newsletters/${newsletterId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchNewsletters();
      }
    } catch (error) {
      console.error('Error deleting newsletter:', error);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.content) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/newsletters/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(templateForm)
      });

      if (res.ok) {
        setShowTemplateModal(false);
        setTemplateForm({ name: '', subject: '', content: '', category: 'general' });
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/newsletters/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const applyTemplate = (template) => {
    setFormData({
      ...formData,
      subject: template.subject,
      content: template.content
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subject: '',
      content: '',
      building_ids: [],
      target_roles: ['coproprietaire'],
      scheduled_at: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sending: 'bg-amber-100 text-amber-700',
      sent: 'bg-green-100 text-green-700'
    };
    const labels = {
      draft: 'Brouillon',
      scheduled: 'Programmée',
      sending: 'Envoi en cours',
      sent: 'Envoyée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="newsletters-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Mail className="text-[#00D3A8]" />
            Newsletters
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Envoyez des communications à vos coproprietaires
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
          data-testid="create-newsletter-btn"
        >
          <Plus size={18} />
          Nouvelle newsletter
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('newsletters')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'newsletters'
              ? 'bg-[#00D3A8] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          Newsletters
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'templates'
              ? 'bg-[#00D3A8] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          Templates
        </button>
      </div>

      {/* Newsletters Tab */}
      {activeTab === 'newsletters' && (
        <>
          {/* Filters */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: 'all', label: 'Toutes' },
              { id: 'draft', label: 'Brouillons' },
              { id: 'scheduled', label: 'Programmées' },
              { id: 'sent', label: 'Envoyées' }
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                  filter === f.id
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {newsletters.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Aucune newsletter</h3>
              <p className="text-gray-500">Créez votre première newsletter pour communiquer avec vos coproprietaires</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Newsletter</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Destinataires</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {newsletters.map((newsletter) => (
                    <tr key={newsletter.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-800">{newsletter.title}</p>
                          <p className="text-sm text-gray-500">{newsletter.subject}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(newsletter.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Users size={14} />
                          {newsletter.recipients_count || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {newsletter.sent_at ? formatDate(newsletter.sent_at) : 
                         newsletter.scheduled_at ? formatDate(newsletter.scheduled_at) : 
                         formatDate(newsletter.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedNewsletter(newsletter);
                              setPreviewData({ preview_html: newsletter.content });
                              setShowPreviewModal(true);
                            }}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                            title="Aperçu"
                          >
                            <Eye size={16} />
                          </button>
                          {newsletter.status === 'draft' && (
                            <button
                              onClick={() => handleSend(newsletter.id)}
                              className="p-2 text-[#00D3A8] hover:bg-teal-50 rounded-lg"
                              title="Envoyer"
                            >
                              <Send size={16} />
                            </button>
                          )}
                          {newsletter.status !== 'sent' && (
                            <button
                              onClick={() => handleDelete(newsletter.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              <Plus size={16} />
              Nouveau template
            </button>
          </div>

          {templates.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Aucun template</h3>
              <p className="text-gray-500">Créez des templates pour gagner du temps</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => {
                const category = CATEGORIES.find(c => c.id === template.category) || CATEGORIES[0];
                return (
                  <div
                    key={template.id}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${category.color}`}>
                        {category.label}
                      </span>
                      {!template.id.startsWith('tpl_') && (
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">{template.name}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{template.subject}</p>
                    <button
                      onClick={() => applyTemplate(template)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] text-sm"
                    >
                      <Copy size={14} />
                      Utiliser ce template
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Newsletter Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Nouvelle newsletter</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title & Subject */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre interne *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    placeholder="Ex: Newsletter Mars 2026"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sujet de l'email *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    placeholder="Ex: Actualités de votre copropriété"
                  />
                </div>
              </div>

              {/* Target */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Immeubles cibles</label>
                  <select
                    multiple
                    value={formData.building_ids}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, opt => opt.value);
                      setFormData({ ...formData, building_ids: values });
                    }}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent h-24"
                  >
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.nom || b.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Laissez vide pour tous les immeubles</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Programmer l'envoi</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide pour sauvegarder en brouillon</p>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenu (HTML) *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent font-mono text-sm"
                  rows={12}
                  placeholder="<div>Votre contenu HTML ici...</div>"
                />
              </div>

              {/* Quick templates */}
              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Templates rapides</label>
                  <div className="flex flex-wrap gap-2">
                    {templates.slice(0, 4).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => applyTemplate(t)}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex flex-col md:flex-row justify-between gap-3">
              <button
                onClick={handlePreview}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Eye size={16} />
                Aperçu
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleCreate(false)}
                  className="px-4 py-2 border border-[#00D3A8] text-[#00D3A8] rounded-lg hover:bg-teal-50"
                >
                  Sauvegarder brouillon
                </button>
                <button
                  onClick={() => handleCreate(true)}
                  className="flex items-center gap-2 px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]"
                >
                  <Send size={16} />
                  Envoyer maintenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Aperçu de la newsletter</h2>
                {previewData.recipients_count !== undefined && (
                  <p className="text-sm text-gray-500">
                    {previewData.recipients_count} destinataire{previewData.recipients_count !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <button
                onClick={() => { setShowPreviewModal(false); setPreviewData(null); setSelectedNewsletter(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div 
                className="border border-gray-200 rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: previewData.preview_html }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Nouveau template</h2>
              <button onClick={() => setShowTemplateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du template *</label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    placeholder="Ex: Convocation AG"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet *</label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  placeholder="Sujet de l'email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenu HTML *</label>
                <textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent font-mono text-sm"
                  rows={10}
                  placeholder="<div>Contenu du template...</div>"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Utilisez des variables: {"{{variable}}"} pour le contenu dynamique
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateTemplate}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]"
              >
                Créer le template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Newsletters;
